import { HardwareItemUnit } from '../enums/hardware-item-unit.enum';

/**
 * Converting between the unit a component is STOCKED in and the unit it is BOUGHT in.
 *
 * Colorant is the case that forced this: production counts it in grams, because that is the unit a
 * shot is measured in, but it is bought from the vendor in pounds. Both numbers are correct and
 * neither can be dropped, so the two have to be able to convert into one another.
 *
 * Conversions are grouped into families. Anything outside a family has no answer — pieces do not
 * convert to pounds — and this returns null there rather than inventing a number, so a caller has to
 * decide what to show instead of silently rendering nonsense.
 */

// everything in a family expressed in one base unit: grams for mass, inches for length
const UNITS_PER_BASE: Partial<Record<HardwareItemUnit, number>> = {
    [HardwareItemUnit.Grams]: 1,
    [HardwareItemUnit.Pounds]: 453.59237,
    [HardwareItemUnit.Ounces]: 28.349523125,

    [HardwareItemUnit.Inches]: 1,
    [HardwareItemUnit.Feet]: 12,
};

const MASS: HardwareItemUnit[] = [HardwareItemUnit.Grams, HardwareItemUnit.Pounds, HardwareItemUnit.Ounces];
const LENGTH: HardwareItemUnit[] = [HardwareItemUnit.Inches, HardwareItemUnit.Feet];

function sameFamily(from: HardwareItemUnit, to: HardwareItemUnit): boolean {
    return (MASS.includes(from) && MASS.includes(to)) || (LENGTH.includes(from) && LENGTH.includes(to));
}

/**
 * Convert a quantity between two hardware units.
 * Returns null when the units are not convertible (different families, or a unit with no factor —
 * pieces, packages and gallons have none).
 */
export function convertHardwareItemUnits(
    value: number,
    from: HardwareItemUnit,
    to: HardwareItemUnit,
): number | null {
    if (from === to) {
        return value;
    }
    if (!sameFamily(from, to)) {
        return null;
    }
    const fromFactor = UNITS_PER_BASE[from];
    const toFactor = UNITS_PER_BASE[to];
    if (!fromFactor || !toFactor) {
        return null;
    }
    return (value * fromFactor) / toFactor;
}

/**
 * The unit a component is ordered in, when that differs from the unit it is stocked in.
 *
 * Grams is the only stocking unit the shop buys in something else — colorant is counted in grams and
 * bought in pounds. Everything else is ordered in the unit it is stocked in, so this returns null and
 * callers show the single figure they already have.
 *
 * This is deliberately NOT read from the vendor row: which package a component comes in is a vendor
 * question answered at order time, but "grams are bought as pounds" is true of the material itself
 * and needs no lookup to state.
 */
export function orderingUnitFor(stockingUnit: HardwareItemUnit): HardwareItemUnit | null {
    return stockingUnit === HardwareItemUnit.Grams ? HardwareItemUnit.Pounds : null;
}
